import { EOlderThanUnit } from 'business/models/Models';
import { IOption } from 'components/input/selectInput';
import { CustomTableColumns, ETableFilterTypes } from './controls/constants';

export const ARCHIVING_NAME_MAX_LENGTH = 50;
export const ARCHIVING_NAME_INVALID_CHARACTERS = ':%<>\\/"*?!,|';
export const ARCHIVING_NAME_INVALID_CHARACTERS_REGEX = /[:%<>\\/"*?!,|]/;
export const ARCHIVING_NAME_INVALID_CHARACTERS_MESSAGE =
  'Name cannot contain any of the following characters: : % < > \\ / " * ? ! , |';
export const ARCHIVING_NAME_DUPLICATE_MESSAGE =
  'This name is already used. Please enter a different name.';

export const ARCHIVING_STORAGE_ERROR_HEADING = 'Error';
export const ARCHIVING_STORAGE_ERROR_TITLE = 'The disk space is full';
export const ARCHIVING_STORAGE_ERROR_DESCRIPTION =
  'The system will continue the process when the disk space is enough for archiving.';
export const ARCHIVING_STORAGE_INVALID_TITLE =
  'The archiving location is invalid or unavailable';
export const ARCHIVING_STORAGE_INVALID_DESCRIPTION =
  'Please configure a valid archiving location before scheduling the archive.';
export const ARCHIVING_STORAGE_FULL_INLINE_MESSAGE = 'The disk space is full!';
// Free space (GB) below which archiving still runs but a low-storage warning is shown.
export const ARCHIVING_STORAGE_LOW_THRESHOLD_GB = 5;
export const ARCHIVING_STORAGE_LOW_MESSAGE = 'Storage space is running low.';
export const ARCHIVING_WAITING_FOR_STORAGE_STATUS = 'WaitingForStorage';
export const ARCHIVING_PAUSED_BY_USER_STATUS = 'Paused';
export const ARCHIVING_STARTING_OVER_STATUS = 'StartingOver';
export const ARCHIVING_STARTED_OVER_STATUS = 'StartedOver';
export const AUTO_REFRESH_PREVIEW_DEBOUNCE_MS = 400;
export const PREVIEW_TABLE_PAGE_SIZE = 13;

export const ARCHIVING_FILTER_APPLIED_MESSAGE = 'Filter applied to Experiment List';
export const ARCHIVING_PATH_SELECTED = 'Archiving path updated';
export const ARCHIVING_FILTER_RESET_MESSAGE =
  'Archiving filter has been reset. Experiment List previewed without filter.';
export const FILTER_CHANGED_WARNING =
  'You have changed your filter selection. Please click ‘Preview’ to update the experiment list.';

export const OlderThanFilterOptions: IOption[] = [
  { value: EOlderThanUnit.Days, title: EOlderThanUnit[EOlderThanUnit.Days] },
  { value: EOlderThanUnit.Months, title: EOlderThanUnit[EOlderThanUnit.Months] },
  { value: EOlderThanUnit.Years, title: EOlderThanUnit[EOlderThanUnit.Years] },
];

export const ResultsTableColumns: CustomTableColumns[] = [
  {
    key: 'name',
    title: 'Experiment Name',
    className: 'w-[18%] max-w-[18%]',
    filterType: ETableFilterTypes.Text,
  },
  {
    key: 'originalLocation',
    title: 'Original Location',
    className: 'w-[16%] max-w-[16%]',
    filterType: ETableFilterTypes.Text,
  },
  {
    key: 'currentLocation',
    title: 'Current Location',
    className: 'w-[18%] max-w-[18%]',
    filterType: ETableFilterTypes.Text,
  },
  {
    key: 'dateCreated',
    title: 'Date Created',
    className: 'w-[12%] max-w-[12%]',
    filterType: ETableFilterTypes.Date,
  },
  {
    key: 'inspectionDate',
    title: 'Last Inspection Date',
    className: 'w-[14%] max-w-[14%]',
    filterType: ETableFilterTypes.Date,
  },
  {
    key: 'dateArchived',
    title: 'Date Archived',
    className: 'w-[12%] max-w-[12%]',
    filterType: ETableFilterTypes.Date,
  },
  {
    key: 'status',
    title: 'Status',
    className: 'w-[10%] max-w-[10%]',
    filterType: ETableFilterTypes.Text,
  },
];

export const PreviewTableColumns: CustomTableColumns[] = [
  {
    key: 'name',
    title: 'Experiment Name',
    className: 'w-[35%] max-w-[35%]',
    filterType: ETableFilterTypes.Text,
  },
  {
    key: 'path',
    title: 'File Location',
    className: 'w-[25%] max-w-[25%]',
    filterType: ETableFilterTypes.Text,
  },
  {
    key: 'dateCreated',
    title: 'Date Created',
    className: 'w-[15%] max-w-[15%]',
    filterType: ETableFilterTypes.Date,
  },
  {
    key: 'inspectionDate',
    title: 'Last Inspection Date',
    className: 'w-[15%] max-w-[15%]',
    filterType: ETableFilterTypes.Date,
  },
  {
    key: 'isArchived',
    title: 'Status',
    className: 'w-[10%] max-w-[10%]',
    filterType: ETableFilterTypes.Text,
  },
];

export enum EArchivingDashboardTab {
  Archiving = 0,
  ArchivedFiles = 1,
}

export const ARCHIVED_FILES_SECTION_PARAM = 'section';
export const ARCHIVED_FILES_SECTION_VALUE = 'archivedFiles';
export const ARCHIVING_RESULTS_TAB_PARAM = 'tab';
export const ARCHIVING_RESULTS_TAB_VALUE = 'results';
export const ARCHIVING_RESULTS_JOB_ID_PARAM = 'jobId';
export const ARCHIVING_RESULTS_PATH = `/setup/archiving?${ARCHIVING_RESULTS_TAB_PARAM}=${ARCHIVING_RESULTS_TAB_VALUE}`;
export const UNARCHIVING_RESULTS_PATH = `${ARCHIVING_RESULTS_PATH}&${ARCHIVED_FILES_SECTION_PARAM}=${ARCHIVED_FILES_SECTION_VALUE}`;
export const ARCHIVING_SCHEDULED_MESSAGE = 'Archiving is scheduled to start at';
export const UNARCHIVING_SCHEDULED_MESSAGE = 'Unarchiving is scheduled to start at';
export const ARCHIVING_IN_PROGRESS_LABEL = 'Archiving Experiment';
export const ARCHIVING_PAUSED_LABEL = 'Archiving Experiment is paused';
export const ARCHIVING_ARCHIVED_PROGRESS_LABEL = 'Archived';
export const UNARCHIVING_IN_PROGRESS_LABEL = 'Restoring Experiment';
export const ARCHIVING_START_OVER_TITLE = 'Start Over Archiving';
export const ARCHIVING_COMPLETED_LABEL = 'Experiment has been archived';
export const UNARCHIVING_COMPLETED_LABEL = 'Experiment has been restored';
export const ARCHIVING_DISMISS_ARIA_LABEL = 'Dismiss archiving notification';
export const UNARCHIVING_DISMISS_ARIA_LABEL = 'Dismiss unarchiving notification';
export const ARCHIVING_PAUSE_MODAL_WARNING_MESSAGE =
  "When you pause the archiving process there is some experiment successfully has been archived and some haven't. You need resume archiving process to complete the archive.";
export const ARCHIVING_PAUSE_MODAL_SUBTITLE =
  'Are you sure you want to pause the archiving process?';
export const ARCHIVING_START_OVER_MODAL_WARNING_MESSAGE =
  'All archiving experiment will store to explorer when you start over the archiving process.';
export const ARCHIVING_START_OVER_MODAL_SUBTITLE = 'Are you sure you want to start over?';
export const ARCHIVING_START_OVER_INCLUDE_ARCHIVED_CHECKBOX_LABEL =
  'Restore Archived Experiments';
